<template>
  <Teleport
    defer
    :to="teleportTo"
  >
    <div
      v-if="isVisible"
      class="tutorial"
      :class="classes"
    >
      <ui-icon
        v-if="enableArrow"
        type="filled"
        class="tutorial__arrow"
        :class="arrowClasses"
        :size="48"
      >
        {{ arrowIcon }}
      </ui-icon>
      <div class="tutorial__wrapper">
        <div class="tutorial__header">
          <div class="tutorial__icon-content">
            <ui-icon v-if="headerIcon">{{ headerIcon }}</ui-icon>
            <slot
              name="tutorial-header"
              :current-step="currentStep"
            ></slot>
          </div>
          <ui-icon
            v-if="isDismissible"
            round
            class="tutorial__close-button"
            @click="closeTutorial"
          >
            close
          </ui-icon>
        </div>
        <!-- tutorial__header -->
        <div class="tutorial__content">
          <slot :current-step="currentStep"></slot>
        </div>
        <!-- tutorial__content -->

        <div class="tutorial__footer">
          <slot name="tutorial-footer">
            <div class="tutorial__counter">
              <span v-if="totalSteps > 1">{{ stepCounter }}</span>
            </div>
            <div class="tutorial__footer-controls">
              <AcvButton
                v-if="isPreviousButtonVisible"
                variant="link"
                @click="decreaseStep"
              >
                <span class="tutorial__footer-button">Previous</span>
              </AcvButton>
              <AcvButton
                variant="link"
                @click="increaseStep"
              >
                <span class="tutorial__footer-button">
                  {{ nextButtonLabel }}
                </span>
              </AcvButton>
            </div>
          </slot>
        </div>
        <!-- tutorial__footer -->
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { AcvButton } from '@acv/shared-vue';

const props = defineProps({
  teleportTo: {
    type: String,
    default: 'body',
  },
  isDismissible: {
    type: Boolean,
    default: true,
  },
  totalSteps: {
    type: Number,
    default: 1,
  },
  classes: {
    type: String,
    default: '',
  },
  arrowClasses: {
    type: String,
    default: '',
  },
  enableArrow: {
    type: Boolean,
    default: true,
  },
  arrowIcon: {
    type: String,
    default: 'arrow_drop_up',
  },
  headerIcon: {
    type: String,
    default: '',
  },
  allowStepBack: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['onFinish', 'onClose']);

/**
 * Track the current step and either increase or decrease
 */
const useFooterControls = () => {
  const { totalSteps } = props;
  const currentStep = ref(1);

  const increaseStep = () => {
    if (currentStep.value === totalSteps) {
      emit('onFinish');
      return;
    }
    currentStep.value++;
  };

  const decreaseStep = () => {
    if (currentStep.value === 1) {
      return;
    }
    currentStep.value--;
  };

  return {
    increaseStep,
    decreaseStep,
    currentStep,
  };
};

/**
 * Toggle the local display state
 * emit onClose event to parent
 */
const useTutorialVisibility = () => {
  const isVisible = ref(true);

  const closeTutorial = () => {
    isVisible.value = false;
    emit('onClose');
  };

  return {
    isVisible,
    closeTutorial,
  };
};

/**
 * Handles step counter and button text
 */
const useTutorialLabels = (currentStep) => {
  const { totalSteps, allowStepBack } = props;
  const stepCounter = computed(() => `${currentStep.value} / ${totalSteps}`);

  const nextButtonLabel = computed(() => {
    let text = 'Got it!';

    if (currentStep.value < totalSteps) {
      text = 'Next';
    } else if (currentStep.value === totalSteps) {
      text = 'Got it!';
    }

    return text;
  });

  const isPreviousButtonVisible = computed(() => allowStepBack && currentStep.value > 1);

  return {
    stepCounter,
    nextButtonLabel,
    isPreviousButtonVisible,
  };
};

const { increaseStep, decreaseStep, currentStep } = useFooterControls();
const { isVisible, closeTutorial } = useTutorialVisibility();
const { stepCounter, nextButtonLabel, isPreviousButtonVisible } = useTutorialLabels(currentStep);
</script>

<style lang="scss" scoped>
.tutorial {
  @apply tw-absolute tw-rounded-lg tw-bg-informational-dark tw-p-5 tw-text-white;

  .tutorial__arrow {
    @apply tw-absolute tw-text-informational-dark;
  }

  .tutorial__header {
    @apply tw-flex tw-items-center tw-justify-between tw-pb-2 tw-text-base;

    .tutorial__icon-content {
      @apply tw-flex tw-items-center tw-gap-3 tw-font-bold tw-uppercase tw-tracking-wide;
    }

    .tutorial__close-button {
      @apply tw-cursor-pointer tw-rounded-full;
      transition: background-color 0.25s ease-in-out;

      &:hover {
        @apply tw-bg-white-380;
      }
    }
  }

  .tutorial__content {
    @apply tw-flex tw-w-full tw-py-2 tw-pl-9 tw-text-left tw-text-sm;
  }

  .tutorial__footer {
    @apply tw-flex tw-w-full tw-items-center tw-justify-between tw-pl-9 tw-pt-4 tw-text-sm;

    .tutorial__footer-controls {
      @apply tw-flex tw-gap-2;
    }

    .tutorial__footer-button {
      @apply tw-rounded tw-p-2 tw-font-sans tw-font-bold tw-tracking-wider tw-text-white;
      transition: background-color 0.25s ease-in-out;

      &:hover {
        @apply tw-bg-white-380;
      }
    }
  }
}
</style>
